import React, {
    ComponentPropsWithoutRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { IoMdAdd } from "react-icons/io";
import OutsideClickHandler from "react-outside-click-handler";
import styled from "styled-components";
import BasicInput from "../basicInput";

// set props to be the same as basic input
interface Props
    extends Omit<
        ComponentPropsWithoutRef<typeof BasicInput>,
        "onChangeHandler"
    > {
    queriedStrings: string[];
    initialSuggestions?: string[];
    onAddHandler?: () => void; // if this is present, defines an add button that performs onAddHandler
    onChangeHandler: (name: string, value: string) => void;
    onEnterSubmit?: () => void;
}

const AutoCompleteSelect: React.FC<Props> = ({
    queriedStrings,
    initialSuggestions,
    name,
    value,
    onAddHandler,
    onChangeHandler,
    onEnterSubmit,
    ...delegated
}) => {
    const [outputStrings, setOutputStrings] = useState<string[]>(
        initialSuggestions || []
    );
    const [highlightedIdx, setHighlightedIdx] = useState<number>(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (value) {
            setHighlightedIdx(0);
            const matchingValues = queriedStrings
                .filter(
                    (string) =>
                        string.toLowerCase().startsWith(value) &&
                        string !== value // i.e. don't match the same word
                )
                .sort();
            setOutputStrings(matchingValues);
            return;
        }
        setOutputStrings(initialSuggestions || []);
    }, [value, queriedStrings, initialSuggestions]);

    const handleSelectSuggestion = useCallback(
        (idx: number) => {
            onChangeHandler(name, outputStrings[idx]);
        },
        [outputStrings, name, onChangeHandler]
    );

    const handleSuggestionClick = useCallback(
        (idx: number) => {
            handleSelectSuggestion(idx);
            inputRef.current?.focus();
        },
        [handleSelectSuggestion]
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Tab" || e.key === "Enter") {
            e.preventDefault();
            if (outputStrings.length !== 0) {
                // pressing tab or enter autocompletes the input with the selected value in the suggestions
                handleSelectSuggestion(highlightedIdx);
                return;
            }
            // in case there are no more options to select
            // call onEnterSubmit on Enter key press if it exists
            if (onEnterSubmit && e.key === "Enter") {
                onEnterSubmit();
                return;
            }
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIdx(highlightedIdx - 1 < 0 ? 0 : highlightedIdx - 1);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIdx(
                highlightedIdx + 1 >= outputStrings.length
                    ? outputStrings.length - 1
                    : highlightedIdx + 1
            );
        }
    };

    const displayedSuggestions = useMemo(() => {
        const mappedSuggestions = outputStrings.map((value, idx) => (
            <DisplayedSuggestion
                isSelected={idx === highlightedIdx}
                key={value}
                onClick={() => handleSuggestionClick(idx)}
            >
                {value}
            </DisplayedSuggestion>
        ));

        return (
            <ScrollContainer reducedWidth={onAddHandler !== undefined}>
                <DisplayedSuggestionContainer>
                    {mappedSuggestions}
                </DisplayedSuggestionContainer>
            </ScrollContainer>
        );
    }, [outputStrings, onAddHandler, highlightedIdx, handleSuggestionClick]);

    return (
        <OutsideClickHandler onOutsideClick={() => setIsFocused(false)}>
            <Container>
                <LayeredContainer squaredCorners={onAddHandler !== undefined}>
                    <BasicInput
                        autoComplete="off"
                        onChangeHandler={(e) =>
                            onChangeHandler(e.target.name, e.target.value)
                        }
                        onFocus={() => setIsFocused(true)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        name={name}
                        value={value}
                        {...delegated}
                    />
                    {onAddHandler ? (
                        <AddButton onClick={() => onAddHandler()}>
                            <IoMdAdd />
                        </AddButton>
                    ) : null}
                </LayeredContainer>
                {outputStrings.length && isFocused
                    ? displayedSuggestions
                    : null}
            </Container>
        </OutsideClickHandler>
    );
};

const Container = styled.div`
    position: relative;

    & > * {
        width: 100%;

        & > *:last-child {
            width: 100%;
        }
    }
`;

const LayeredContainer = styled.div<{ squaredCorners: boolean }>`
    display: flex;

    & > * {
        &:first-child {
            flex: 1;
            border-radius: ${(props) =>
                props.squaredCorners ? "0.2rem 0 0 0.2rem" : "none"};
            border-right: ${(props) =>
                props.squaredCorners
                    ? "none"
                    : `solid 1px ${props.theme.colors.neutral[300]}`};
        }
        &:nth-child(2) {
            flex: 0 0 6rem;
        }
    }
`;

const AddButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 0 0.2rem 0.2rem 0;
    border: solid 1px ${(props) => props.theme.colors.neutral[300]};
    border-left: none;

    background: ${(props) => props.theme.colors.neutral[200]};
    color: ${(props) => props.theme.colors.neutral[500]};

    &:hover {
        cursor: pointer;
        background: ${(props) => props.theme.colors.neutral[100]};
    }
`;

const DisplayedSuggestion = styled.div<{ isSelected: boolean }>`
    padding: 0.5rem;
    user-select: none;
    min-height: 2rem;
    height: fit-content;
    max-width: inherit;

    overflow: hidden;
    word-break: break-word;

    display: flex;
    align-items: center;

    background: ${(props) =>
        props.isSelected ? props.theme.colors.neutral[100] : "white"};
    color: ${(props) => props.theme.colors.neutral[600]};

    &:hover {
        background: ${(props) =>
            props.isSelected
                ? props.theme.colors.neutral[200]
                : props.theme.colors.neutral[150]};
    }
`;

const ScrollContainer = styled.div<{ reducedWidth: boolean }>`
    position: absolute;
    display: flex;
    z-index: 2;
    max-height: 18rem;
    overflow: hidden;
    max-width: ${(props) =>
        props.reducedWidth ? "calc(100% - 6rem)" : "100%"};
`;

const DisplayedSuggestionContainer = styled.div`
    border: solid 0.075rem ${(props) => props.theme.colors.neutral[300]};
    border-top: none;

    overflow: auto;
`;

export default AutoCompleteSelect;
